import React, { useEffect, useState } from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import TagManager from 'react-gtm-module';
import config from './config';
import { loadUser, setOnFocusActiveFacility, user } from './redux/user';
import { error } from './redux/error';
import Notification from './components/notification/Notification';
import LoadingSpinner from './components/spinner/LoadingSpinner';
import Login from './features/login/Login';
import ForgotPassword from './features/login/ForgotPassword';
import NewPassword from './features/login/NewPassword';
import Form530Download from './features/form-530/Form530Download';
import NotFound from './features/error/NotFound';
import PrivateSection from './sections/PrivateSection';
import useTabActive from './hooks/useTabActive';
import useBroadcast from './hooks/useBroadcast';
import { BROADCAST_CHANNELS, ERROR_CODES } from './constants';
import SingleActionModal from './components/modals/SingleActionModal';

if (process.env.REACT_APP_GTM_ID) {
	TagManager.initialize({
		gtmId: process.env.REACT_APP_GTM_ID
	});
}

const App = () => {
	const dispatch = useDispatch();
	const userData = useSelector(user, shallowEqual);
	const err = useSelector(error, shallowEqual);
	const isTabActive = useTabActive();
	const [hasActiveFacilityChanged, setHasActiveFacilityChanged] = useBroadcast(
		BROADCAST_CHANNELS.ACTIVE_FACILITY,
		false
	);
	const [showRefreshModal, setShowRefreshModal] = useState(false);

	useEffect(() => {
		dispatch(loadUser());
	}, [dispatch]);

	useEffect(() => {
		if (err && err.type === '403' && !window.location.pathname.startsWith('/private/')) {
			window.location.reload();
		}
		if (err?.type === ERROR_CODES.ACTIVE_FACILITY_CHANGE_FAILED) {
			setShowRefreshModal(true);
		}
	}, [err]);

	useEffect(() => {
		if (isTabActive && userData.activeFacility && hasActiveFacilityChanged) {
			dispatch(setOnFocusActiveFacility(userData.activeFacility.id));
			setHasActiveFacilityChanged(true);
		}
	}, [isTabActive, userData, hasActiveFacilityChanged, dispatch]);

	const loggedIn = !!userData.name && (userData.facilitiesEnabled?.length > 0 || userData.agencyId);
	const userLoaded = userData.loaded;

	return (
		<div className={classNames('App', { 'ie-10': config.IS_IE })}>
			<Notification />
			{userLoaded ? (
				<Switch>
					<PublicRoute path="/login" exact component={Login} loggedIn={loggedIn} />
					<PublicRoute path="/forgot-password" exact component={ForgotPassword} loggedIn={loggedIn} />
					<PublicRoute path="/new-password/:verificationToken" exact component={NewPassword} loggedIn={loggedIn} />
					<PrivateRoute path="/private" component={PrivateSection} userData={userData} loggedIn={loggedIn} />
					<PrivateRoute
						path="/form-530/:facilityId/:fileId"
						exact
						userData={userData}
						component={Form530Download}
						loggedIn={loggedIn}
					/>
					<Route
						path="/"
						exact
						render={() => {
							if (userData?.agencyId) {
								return <Redirect to="/private/agency/shifts" />;
							}
							return userData?.flexGlobalOverviewEnabled || userData?.onDemandGlobalOverviewEnabled ? (
								<Redirect to="/private/global-overview" />
							) : (
								<Redirect to="/private/shifts" />
							);
						}}
					/>
					<Route path="/404" render={() => <NotFound userData={userData} />} />
					<Route path="/" render={() => <Redirect to="/404" />} />
				</Switch>
			) : (
				<LoadingSpinner />
			)}
			{showRefreshModal && (
				<SingleActionModal
					confirmModal
					title="Refresh the Page"
					actionLabel="Refresh Now"
					onAction={() => {
						setShowRefreshModal(false);
						window.location.reload();
					}}
					className="refresh-modal"
					onHide={() => setShowRefreshModal(false)}
					disableClickOutside
				>
					An error occurred while loading the facility information. Refresh now to continue.
				</SingleActionModal>
			)}
		</div>
	);
};

export default App;

const PublicRoute = ({ component: Component, ...rest }) => (
	// eslint-disable-next-line react/jsx-props-no-spreading
	<Route {...rest} render={(props) => (!rest.loggedIn ? <Component {...props} /> : <Redirect to="/" />)} />
);

const PrivateRoute = ({ component: Component, ...rest }) => (
	// eslint-disable-next-line react/jsx-props-no-spreading
	<Route {...rest} render={() => (rest.loggedIn ? <Component {...rest} /> : <Redirect to="/login" />)} />
);
